import React, { useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { Menu, MenuItem, Checkbox, Button, Divider, Typography } from '@material-ui/core';

export interface MenuItemData {
  label: string;
  value: any;
  checked: boolean;
  badge?: {
    text: string;
    confidence: number;
  };
}

interface SplitButtonWithMenuProps {
  // Main button props
  label: string;
  onClick: () => void;
  variant: 'keep' | 'change' | 'accept';
  selected?: boolean;
  hasSelection?: boolean;
  disabled?: boolean;
  processing?: boolean;

  // Menu props
  menuItems: MenuItemData[];
  onMenuItemsSelect: (selectedValues: any[]) => void;

  // Optional customization
  className?: string;
}

const SplitButtonWithMenu: React.FC<SplitButtonWithMenuProps> = ({
  label,
  onClick,
  variant,
  selected = false,
  hasSelection = false,
  disabled = false,
  processing = false,
  menuItems,
  onMenuItemsSelect,
  className,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedValues, setSelectedValues] = useState<any[]>(
    menuItems.filter((item) => item.checked).map((item) => item.value)
  );

  // Reset selected values when menu opens
  React.useEffect(() => {
    if (anchorEl) {
      setSelectedValues(menuItems.filter((item) => item.checked).map((item) => item.value));
    }
  }, [anchorEl, menuItems]);

  const toggleSelection = (value: any) => {
    setSelectedValues((prev) => {
      if (prev.includes(value)) {
        return prev.filter((v) => v !== value);
      } else {
        return [...prev, value];
      }
    });
  };

  const handleApply = () => {
    if (selectedValues.length > 0) {
      onMenuItemsSelect(selectedValues);
      setAnchorEl(null);
    }
  };

  const handleCancel = () => {
    setAnchorEl(null);
  };

  // If only one or no items, render simple button
  if (menuItems.length <= 1) {
    return (
      <ActionButton
        onClick={onClick}
        disabled={disabled}
        $selected={selected}
        $hasSelection={hasSelection}
        $variant={variant}
        data-selected={selected ? 'true' : 'false'}
        className={className}
      >
        {processing ? <i className="fa fa-spinner fa-spin" /> : label}
      </ActionButton>
    );
  }

  // Render split button with dropdown
  return (
    <SplitButtonContainer className={className}>
      <ActionButton
        onClick={onClick}
        disabled={disabled}
        $selected={selected}
        $hasSelection={hasSelection}
        $variant={variant}
        $isLeftButton
        data-selected={selected ? 'true' : 'false'}
      >
        {processing ? <i className="fa fa-spinner fa-spin" /> : label}
      </ActionButton>
      <DropdownButton
        onClick={(e) => setAnchorEl(e.currentTarget)}
        disabled={disabled}
        $selected={selected}
        $variant={variant}
        title="Select items"
      >
        <FontAwesomeIcon icon={faChevronDown} />
      </DropdownButton>
      <StyledMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCancel}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuHeader>
          <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
            Select Items to Apply
          </Typography>
        </MenuHeader>
        <Divider />
        <MenuContent>
          {menuItems.map((item, index) => (
            <StyledMenuItem key={index} onClick={() => toggleSelection(item.value)} dense>
              <Checkbox
                checked={selectedValues.includes(item.value)}
                color="primary"
                size="small"
                style={{ padding: '0 8px 0 0' }}
              />
              <ItemContent>
                <ItemLabel>{item.label}</ItemLabel>
                {item.badge && (
                  <Badge $confidence={item.badge.confidence}>{item.badge.text}</Badge>
                )}
              </ItemContent>
            </StyledMenuItem>
          ))}
        </MenuContent>
        <Divider />
        <MenuFooter>
          <Button onClick={handleCancel} size="small" style={{ marginRight: 8 }}>
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            variant="contained"
            color="primary"
            size="small"
            disabled={selectedValues.length === 0}
          >
            Apply ({selectedValues.length})
          </Button>
        </MenuFooter>
      </StyledMenu>
    </SplitButtonContainer>
  );
};

// Styled Components

const SplitButtonContainer = styled.div`
  position: relative;
  display: flex;
  align-items: stretch;
`;

const ActionButton = styled.button<{
  $selected?: boolean;
  $variant?: 'keep' | 'change' | 'accept';
  $hasSelection?: boolean;
  $isLeftButton?: boolean;
}>`
  padding: 6px 14px;
  background-color: ${(p) =>
    p.$selected
      ? p.$variant === 'keep'
        ? '#66BB6A'
        : p.$variant === 'change'
        ? '#42A5F5'
        : '#8E24AA'
      : p.$variant === 'keep'
      ? '#e8f5e9'
      : p.$variant === 'change'
      ? '#e3f2fd'
      : '#f3e5f5'};
  color: ${(p) =>
    p.$selected ? '#fff' : p.$variant === 'keep' ? '#2e7d32' : p.$variant === 'change' ? '#1565c0' : '#6a1b9a'};
  border: 1px solid
    ${(p) =>
      p.$selected
        ? 'transparent'
        : p.$variant === 'keep'
        ? '#81c784'
        : p.$variant === 'change'
        ? '#64b5f6'
        : '#ce93d8'};
  border-radius: ${(p) => (p.$isLeftButton ? '6px 0 0 6px' : '6px')};
  border-right: ${(p) => (p.$isLeftButton ? 'none' : '1px solid')};
  cursor: ${(p) => (p.$selected ? 'default' : 'pointer')};
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s ease;
  opacity: ${(p) => (p.$selected ? 1 : p.$hasSelection ? 0.3 : 1)};
  min-width: 74px;
  text-align: center;
  pointer-events: ${(p) => (p.$selected ? 'none' : 'auto')};
  &:hover:not(:disabled):not([data-selected='true']) {
    opacity: 1;
    background-color: ${(p) => (p.$variant === 'keep' ? '#66BB6A' : p.$variant === 'change' ? '#42A5F5' : '#8E24AA')};
    color: #fff;
  }
  &:disabled {
    cursor: wait;
    opacity: 0.5;
  }
`;

const DropdownButton = styled.button<{
  $selected?: boolean;
  $variant?: 'keep' | 'change' | 'accept';
}>`
  padding: 6px 8px;
  background-color: ${(p) =>
    p.$selected
      ? p.$variant === 'keep'
        ? '#66BB6A'
        : p.$variant === 'change'
        ? '#42A5F5'
        : '#8E24AA'
      : p.$variant === 'keep'
      ? '#e8f5e9'
      : p.$variant === 'change'
      ? '#e3f2fd'
      : '#f3e5f5'};
  color: ${(p) =>
    p.$selected ? '#fff' : p.$variant === 'keep' ? '#2e7d32' : p.$variant === 'change' ? '#1565c0' : '#6a1b9a'};
  border: 1px solid
    ${(p) =>
      p.$selected
        ? 'transparent'
        : p.$variant === 'keep'
        ? '#81c784'
        : p.$variant === 'change'
        ? '#64b5f6'
        : '#ce93d8'};
  border-radius: 0 6px 6px 0;
  border-left: 1px solid
    ${(p) =>
      p.$selected
        ? 'rgba(255, 255, 255, 0.3)'
        : p.$variant === 'keep'
        ? '#a5d6a7'
        : p.$variant === 'change'
        ? '#90caf9'
        : '#e1bee7'};
  cursor: pointer;
  font-size: 11px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background-color: ${(p) =>
      p.$variant === 'keep' ? '#66BB6A' : p.$variant === 'change' ? '#42A5F5' : '#8E24AA'};
    color: #fff;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const StyledMenu = styled(Menu)`
  .MuiPaper-root {
    min-width: 320px;
    max-width: 480px;
    max-height: 500px;
  }
`;

const MenuHeader = styled.div`
  padding: 12px 16px;
`;

const MenuContent = styled.div`
  max-height: 300px;
  overflow-y: auto;
`;

const StyledMenuItem = styled(MenuItem)`
  padding: 8px 16px !important;
  display: flex !important;
  align-items: flex-start !important;
`;

const ItemContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const ItemLabel = styled.div`
  color: #333;
  font-size: 13px;
  line-height: 1.4;
  word-wrap: break-word;
  flex: 1;
`;

const Badge = styled.span<{ $confidence: number }>`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  padding: 2px 6px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 11px;
  line-height: 1.4;
  background-color: ${(props) => {
    const conf = props.$confidence;
    if (conf >= 0.8) return '#e8f5e9';
    if (conf >= 0.6) return '#fff3e0';
    return '#ffebee';
  }};
  color: ${(props) => {
    const conf = props.$confidence;
    if (conf >= 0.8) return '#2e7d32';
    if (conf >= 0.6) return '#ef6c00';
    return '#c62828';
  }};
  border: 1px solid
    ${(props) => {
      const conf = props.$confidence;
      if (conf >= 0.8) return '#a5d6a7';
      if (conf >= 0.6) return '#ffcc80';
      return '#ef9a9a';
    }};
`;

const MenuFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 8px 12px;
`;

export default SplitButtonWithMenu;
